/* global google */

import {memo, useCallback, useContext} from 'react'

import {useUserConsents} from '@s-ui/consents'
import Context from '@s-ui/react-context'
import {useMount, useMountedState} from '@s-ui/react-hooks'
import GigyaSocialNotifier from '@adv-mt/mt-gigya-social-notifier'
import GoogleOneTapLogin from '@adv-mt/mt-google-one-tap-login'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import useComponentState from './useComponentState.js'

const GOOGLE_ONE_TAP_HIT_ORIGIN = 'google one tap'

const LoginGoogleOneTapToGigyaConnector = () => {
  const {domain} = useContext(Context)
  const {track} = useAnalyticsTrack()
  const config = domain.get('config')

  const {
    CONSENTS: {GOOGLE_ONE_TAP: gotRequiredConsents}
  } = config.get('COMMON')

  const {
    TRACKING_EVENT_NAMES: {SIGNED_IN, SIGNED_UP, SIGNED_IN_ERROR, SIGNED_UP_ERROR}
  } = config.get('tracking')

  const browserName = config.get('browserName')
  const osName = config.get('osName')
  const CHROME_BROWSER_NAME = config.get('CHROME_BROWSER_NAME')
  const IOS_OS_NAME = config.get('IOS_OS_NAME')

  const isIOS = osName === IOS_OS_NAME
  const isChrome = browserName === CHROME_BROWSER_NAME

  const hasConsentsForGot = useUserConsents(gotRequiredConsents)

  const [state, actions] = useComponentState()

  const {isLogged, isAuthenticationVerified, isConcluded, googleAccessToken} = state

  const {setIsLogged, setIsConcluded, setGoogleAccessToken} = actions

  const getMountedState = useMountedState()
  const isMounted = getMountedState()

  useMount(async () => {
    const {logged} = await domain.get('verify_authentication_use_case').execute()

    setIsLogged(Boolean(logged))
  })

  const authenticateOnSessionStarter = async ({gigyaResponse, isNewUser}) => {
    try {
      await domain.get('login_authentication_use_case').execute({gigyaResponse})

      const trackingEventName = isNewUser ? SIGNED_UP : SIGNED_IN

      const trackingEvent = await domain.get('get_event_tracking_data_use_case').execute({
        event: trackingEventName,
        origin: GOOGLE_ONE_TAP_HIT_ORIGIN
      })

      trackingEvent && track(trackingEvent)
    } catch (error) {
      console.error(error)

      const trackingErrorEventName = isNewUser ? SIGNED_UP_ERROR : SIGNED_IN_ERROR

      const trackingErrorEvent = await domain.get('get_event_tracking_data_use_case').execute({
        event: trackingErrorEventName,
        origin: GOOGLE_ONE_TAP_HIT_ORIGIN,
        context: 'session-starter authentication'
      })

      track(trackingErrorEvent)
    }
  }

  const handleOnGoogleOneTapPrompt = useCallback(() => {
    setIsConcluded()
  }, [setIsConcluded])

  const handleOnGoogleAccessToken = useCallback(
    accessToken => {
      setGoogleAccessToken(accessToken)
    },
    [setGoogleAccessToken]
  )

  const handleOnGigyaLinkAccountModalClose = useCallback(() => {
    google && google.accounts.id.disableAutoSelect()
  }, [])

  const handleOnGigyaAuthentication = useCallback(
    ({gigyaResponse, isNewUser}) => {
      authenticateOnSessionStarter({gigyaResponse, isNewUser})
      setIsConcluded()
    },
    [] //eslint-disable-line
  )

  const hasGoogleAccesToken = Boolean(googleAccessToken)

  return isMounted && hasConsentsForGot && isAuthenticationVerified && !isLogged && isChrome && !isIOS ? (
    <>
      <GoogleOneTapLogin
        onPrompt={handleOnGoogleOneTapPrompt}
        onAccessToken={handleOnGoogleAccessToken}
        prompted={isConcluded}
        origin={GOOGLE_ONE_TAP_HIT_ORIGIN}
      />
      {hasGoogleAccesToken && !isConcluded ? (
        <GigyaSocialNotifier
          loginConfig={{
            providerSessions: {
              googlePlus: {
                authToken: googleAccessToken
              }
            }
          }}
          onModalClose={handleOnGigyaLinkAccountModalClose}
          onAuthentication={handleOnGigyaAuthentication}
          notified={isConcluded}
          origin={GOOGLE_ONE_TAP_HIT_ORIGIN}
        />
      ) : null}
    </>
  ) : null
}

LoginGoogleOneTapToGigyaConnector.displayName = 'LoginGoogleOneTapToGigyaConnector'

export default memo(LoginGoogleOneTapToGigyaConnector)
